import { CircularProgress, Container } from '@mui/material';
import { Tooltip, Box } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaStates } from '../../../commands/practitioner/enums/MediaStates';
import { MediaTypes } from '../../../commands/practitioner/enums/MediaTypes';
import { PlaybackType } from '../../../commands/practitioner/enums/PlaybackType';
import useAppContext from '../../../hooks/useAppContext/useAppContext';
import { SourceProjection } from '../../../commands/practitioner/enums/SourceProjection';
import usePractitionerControlsContext from '../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import BackendService from '../../../services/BackendService';
import { startTimer, mediaKey } from '../../../metrics/sentryMetrics';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import { Border } from '../ToggleVideoEMDR/ToggleVideoEMDR';
import './MediaControls.scss';
import useMediaContext from '../../../hooks/useMediaContextHook/useMediaContext';
import Play from '../../../assets/mainControls/IconPlay.svg?react';
import Pause from '../../../assets/mediaControls/IconPause.svg?react';
import Load from '../../../assets/mediaControls/multi-media.svg?react';
import Visible from '../../../assets/mediaControls/visible.svg?react';
import Invisible from '../../../assets/mediaControls/invisible.svg?react';
import Muted from '../../../assets/mediaControls/muted.svg?react';
import Unmuted from '../../../assets/mediaControls/unmuted.svg?react';
import ErrorDialog from '../../ErrorDialog/ErrorDialog';
import { TwilioError } from 'twilio-video';
import useCurrentSessionState from '../../../hooks/useCurrentSessionState/useCurrentSessionState';
import { commandVersion1_2_0, isVersionLessThan } from '../../../utils/versionCheck';

interface MediaControlsProps {
  modalIsOpened: boolean;
  mediaState: number;
  setModalIsOpened: Dispatch<SetStateAction<boolean>>;
  setConnectionReminder?: Dispatch<SetStateAction<boolean>>;
}

const MediaControls: React.FC<MediaControlsProps> = ({
  modalIsOpened,
  setModalIsOpened,
  mediaState,
  setConnectionReminder,
}) => {
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const {
    practitionerMediaURI,
    setPractitionerMediaURI,
    isMediaLoaded,
    isConfirming,
    setIsConfirming,
    isPractitionerPlayerVisible,
    setIsPractitionerPlayerVisible,
    isPractitionerReactPlayerStarted,
    setIsPractitionerReactPlayerStarted,
    hasBeenSaved,
    setHasBeenSaved,
    taskHasStarted,
    setTaskHasStarted,
    practitionerMediaType,
    setIsMutedYoutube,
    isMutedYoutube,
    setSavedUri,
    projectionType,
    startResetTimeout,
    setStartResetTimeout,
    practitionerVideoTime,
  } = usePractitionerControlsContext();
  const { t } = useTranslation();
  const { setDiveIndex, setSelectedItemURL, setFirstDiveInd } = useMediaContext();
  const currentState = useCurrentSessionState() || {};
  const { isConnected: isDeviceConnected } = currentState as any;
  const tooltipUpload = t('upload-media');
  const tooltipEditMedia = t('edit-media');
  const tooltipShowMedia = t('show-media');
  const tooltipHideMedia = t('hide-media');
  const tooltipStart = t('start-media');
  const tooltipPause = t('pause-media');
  const tooltipStartDisabled = t('no-available-media');
  const [error, setError] = useState<string>('');
  const isMediaUriAvailable = Boolean(isMediaLoaded && hasBeenSaved);
  const basePath = 'assets/icons';
  const muteIcon = isMutedYoutube ? `${basePath}/muted.svg` : `${basePath}/unmuted.svg`;
  const prevConnectedRef = useRef<boolean | undefined>(undefined);
  const hasSeenNonSuccessfulRef = useRef<boolean>(false);
  const [isOnline, setIsonline] = useState<boolean>(navigator.onLine);
  const mediaTypeTitle =
    practitionerMediaType === MediaTypes.VIDEO
      ? 'video'
      : practitionerMediaType === MediaTypes.AUDIO
        ? 'audio'
        : 'image';

  const startTooltip =
    isMediaUriAvailable && practitionerMediaType !== MediaTypes.IMAGE
      ? isPractitionerReactPlayerStarted
        ? tooltipPause
        : tooltipStart
      : tooltipStartDisabled;
  const soundTooltip =
    isMediaUriAvailable && (practitionerMediaType === MediaTypes.VIDEO || practitionerMediaType === MediaTypes.AUDIO)
      ? !isMutedYoutube
        ? t('mute')
        : t('unmute')
      : t('noSoundOptions');

  const handleOpenModal = async () => {
    if (typeof document !== 'undefined') {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
    if (isPractitionerPlayerVisible) {
      setIsConfirming(true);
      return;
    }
    await resetAllPreviousData();
    setHasBeenSaved(false);
    setModalIsOpened((prevState) => !prevState);
    setPractitionerMediaURI('');
  };

  const isOldVersion =
    typeof currentState.version === 'string' && isVersionLessThan(currentState.version, commandVersion1_2_0);

  const resetAllPreviousData = async () => {
    await BackendService.sendMediaStatusReport(sessionId, treatmentId, '', 0, 0);
    await BackendService.sendLoadMedia(sessionId, treatmentId, '', practitionerMediaType, projectionType, 'Off');
  };

  const handleConfirm = async () => {
    // Clear any old media that was loaded
    await BackendService.sendMediaStatusReport(sessionId, treatmentId, '', 0, 0);
    await BackendService.sendLoadMedia(sessionId, treatmentId, '', MediaTypes.VIDEO, SourceProjection.Flat, 'Off');
    await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, false);
    await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, PlaybackType.Pause, 0);
    setHasBeenSaved(false);
    setIsConfirming(false);
    setModalIsOpened(true);
    setIsPractitionerPlayerVisible(false);
    setIsPractitionerReactPlayerStarted(false);
    setPractitionerMediaURI('');
    setIsMutedYoutube(true);
    setSavedUri('');
    setDiveIndex([]);
    setSelectedItemURL('');
    setFirstDiveInd(true);
  };

  const handleVisibleClicked = async () => {
    if (!isMediaUriAvailable || practitionerMediaType === MediaTypes.AUDIO) {
      return null;
    }
    const newVisibilityState = !isPractitionerPlayerVisible;
    setIsPractitionerPlayerVisible(newVisibilityState);
    await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, newVisibilityState);

    if (isPractitionerReactPlayerStarted) {
      await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, PlaybackType.Pause, 0);
      setIsPractitionerReactPlayerStarted(false);
    }
  };

  const handleStart = async () => {
    if (!isMediaUriAvailable || practitionerMediaType === MediaTypes.IMAGE) {
      return;
    }
    const newStartedState = !isPractitionerReactPlayerStarted;
    setIsPractitionerReactPlayerStarted(newStartedState);
    if (sessionId && treatmentId && practitionerMediaURI && hasBeenSaved) {
      // jump to synchronized player.
      if (newStartedState && practitionerVideoTime > 0) {
        await BackendService.sendMediaCommand(
          sessionId,
          treatmentId,
          practitionerMediaURI,
          PlaybackType.Jump,
          practitionerVideoTime
        );
      }

      const playbackType = newStartedState ? PlaybackType.Play : PlaybackType.Pause;
      if (newStartedState) {
        // Start E2E timer; will be ended when client reports MLSR:Successful
        startTimer(mediaKey(practitionerMediaURI));
      }
      await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, playbackType, 0);
      if (newStartedState) {
        await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, true);
        setIsPractitionerPlayerVisible(true);
      }
      if (taskHasStarted && hasDevice && isOldVersion) {
        setTimeout(() => {
          setTaskHasStarted(false);
        }, 150);
      }
    }
  };

  const isLoadingMedia = useMemo(() => {
    if (!practitionerMediaURI || !isMediaLoaded || !hasBeenSaved) return false;
    return startResetTimeout;
  }, [practitionerMediaURI, isMediaLoaded, hasBeenSaved, startResetTimeout]);

  useEffect(() => {
    // fired any time network status changes
    const updateStatus = () => setIsonline(navigator.onLine);

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    // call once in case the tab was opened while offline
    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  useEffect(() => {
    if (!startResetTimeout) {
      hasSeenNonSuccessfulRef.current = false;
      return; // 1. guard
    }

    /** Re-use this for any early-exit error case */
    const handleError = async (msg: string) => {
      await resetAllPreviousData();
      setPractitionerMediaURI('');
      setError(msg); // ← you forgot this line
      setStartResetTimeout(false);
    };

    /* RETURN if offline */
    if (!isOnline) {
      handleError(t('user-is-offline'));
      return;
    }

    const resetTimeout = setTimeout(() => {
      void handleError((currentState as any)?.errorMessage || t('no-load-response'));
    }, 31_000);

    // Ignore a stale Successful left over from a previous load — only accept
    // Successful as terminal after we've observed at least one non-Successful
    // poll for the current load cycle. Tracked via ref so the flag persists
    // across effect re-runs (a `let` would get reset each time mediaState
    // changes and the effect re-fires).
    if (mediaState !== MediaStates.Successful) {
      hasSeenNonSuccessfulRef.current = true;
    }

    /*  POLL UNTIL LOAD COMPLETE */
    const interval = setInterval(() => {
      if (mediaState !== MediaStates.Successful) {
        hasSeenNonSuccessfulRef.current = true;
      }
      if (mediaState === MediaStates.Successful && hasSeenNonSuccessfulRef.current) {
        clearTimeout(resetTimeout);
        clearInterval(interval);
        setStartResetTimeout(false);
      } else if (mediaState === MediaStates.Failure) {
        // Stop polling immediately on failure and show the specific error when present
        clearTimeout(resetTimeout);
        clearInterval(interval);
        void handleError((currentState as any)?.errorMessage || t('no-load-response'));
      }
    }, 1_000);

    return () => {
      clearTimeout(resetTimeout);
      clearInterval(interval);
    };
  }, [startResetTimeout, mediaState, isOnline, (currentState as any)?.errorMessage]);

  useEffect(() => {
    // trigger a media player stop and show a connection error modal when playback is active and the glasses are disconnecte
    if (!hasDevice) return;

    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = isDeviceConnected;

    if (wasConnected === undefined) return;

    const connectionDropped = wasConnected && !isDeviceConnected;
    if (!connectionDropped) return;

    const mediaActive =
      isPractitionerReactPlayerStarted || isPractitionerPlayerVisible || (!!practitionerMediaURI && hasBeenSaved);

    if (mediaActive) {
      setIsPractitionerPlayerVisible(false);
      setConnectionReminder?.(true);
      void resetAllPreviousData();
    }
  }, [isDeviceConnected, hasDevice]);

  return (
    <Border>
      {error ? <ErrorDialog dismissError={() => setError('')} error={new Error(error) as TwilioError} /> : null}
      <ConfirmModal
        title={t('confirm-title')}
        message={t('confirm-message')}
        open={isConfirming}
        closeAction={() => setIsConfirming(false)}
        confirmAction={handleConfirm}
      />
      <div className={'media-controls-container'}>
        <Container className="detail-container">
          <div className={'media-controls-options-container container-label'}>
            <h2>{t('media')} </h2>
            <h2 className={'media-type'}> {t(`load-play ${mediaTypeTitle}`)}</h2>
          </div>
          <div className={'media-controls-options-container'}>
            <div className="tooltip-container" onClick={handleOpenModal} data-testid="pwid-media-load">
              <Tooltip title={tooltipEditMedia}>
                <span className="span">
                  <button>
                    <Load className="load icon-sign" />
                  </button>
                </span>
              </Tooltip>
            </div>
            {isLoadingMedia && practitionerMediaType !== MediaTypes.IMAGE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.IMAGE && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable || !hasBeenSaved ? 'isDisabled' : ''}`}
                  onClick={handleStart}
                  data-testid="pwid-media-play"
                >
                  <Tooltip title={startTooltip}>
                    <span className="span">
                      <button disabled={!isMediaUriAvailable || !hasBeenSaved}>
                        {!isPractitionerReactPlayerStarted ? (
                          <Play className="icon-sign" />
                        ) : (
                          <Pause className="icon-sign" />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
            {isLoadingMedia && practitionerMediaType !== MediaTypes.AUDIO ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.AUDIO && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable ? 'isDisabled' : ''}`}
                  onClick={handleVisibleClicked}
                  data-testid="pwid-media-visibility"
                >
                  <Tooltip title={isPractitionerPlayerVisible ? tooltipHideMedia : tooltipShowMedia}>
                    <span className="span">
                      <button disabled={!isMediaUriAvailable}>
                        {' '}
                        {isPractitionerPlayerVisible ? (
                          <Visible className={'visible icon-sign'} />
                        ) : (
                          <Invisible className={'hide icon-sign'} />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
            {isLoadingMedia && practitionerMediaType !== MediaTypes.IMAGE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.IMAGE && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable || !hasBeenSaved ? 'isDisabled' : ''}`}
                  data-testid="pwid-media-mute"
                >
                  <Tooltip
                    title={soundTooltip}
                    placement="top"
                    onClick={() => setIsMutedYoutube((prevState) => !prevState)}
                  >
                    <span className="span">
                      <button disabled={!hasBeenSaved && !isMediaUriAvailable}>
                        {isMutedYoutube ? (
                          <Muted className="muted icon-sign" />
                        ) : (
                          <Unmuted className="unmuted icon-sign" />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
          </div>
        </Container>
      </div>
    </Border>
  );
};

export default MediaControls;
